import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  FriendlyError,
  MoodQuadrantGrid,
  MOOD_QUADRANTS,
  SectionLoading,
  StepHeading,
  StepShell,
  WordChips,
} from "@codel/dova-ds";
import { TextField } from "../../components/TextField";
import { Button } from "../../onboarding/components/Button";
import { useMeContext } from "../../MeApp";
import { unexpectedDetail, unexpectedErrorDetail } from "../../expectedOutcome";
import {
  isAlreadySubmitted,
  useCheckinState,
  useSaveCheckin,
  type CheckinDraft,
  type MoodQuadrant,
} from "../../hooks/reflectionHooks";
import { useSession } from "../../hooks/sessionHooks";
import { FlowOutcomeScreen, FlowSavedScreen } from "../FlowOutcomeScreen";
import { useFlowBack } from "../useFlowBack";
import { classifyCheckinError } from "./checkinOutcome";
import { CHECKIN_CLOSED, CHECKIN_COPY, FALLBACK_THERAPIST_NAME, privacyLine, type CheckinQuestion } from "./checkinCopy";
import { COUPLE_EVENTS } from "../../analytics/events";
import { track, useTrackOnce } from "../../analytics/track";

// CheckinFlow — the pre-session check-in (design: presession.jsx). Four screens,
// steps as COMPONENT STATE (contracts §4.1 — never sub-routes):
//   1. how you're feeling: quadrant → feeling word (required)
//   2–4. three written questions, one per screen (each optional)
//   → saved (SuccessMoment).
// Private to the acting partner; the first step's privacy line says who sees the
// answers. Answers are FINAL once saved: re-entry after saving goes straight
// to the saved screen, and a racing second save (409 already submitted) lands
// there too, since the answers it would have written are already saved.

export function CheckinFlow() {
  const { sessionUuid = "" } = useParams();
  const navigate = useNavigate();
  const { couple } = useMeContext();
  const state = useCheckinState(sessionUuid);
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;

  if (state.isLoading) return <SectionLoading />;
  if (state.error) {
    return (
      <StepShell>
        {/* Handed the server's reason, not just `ApiError`'s bare "HTTP 404:" status line. */}
        <FriendlyError detail={unexpectedDetail(state.error)} />
      </StepShell>
    );
  }
  // Saved wins over a closed window: after the session starts, a saved check-in is still saved.
  if (state.data?.checkin) return <CheckinSaved />;
  // A closed window is a gate, not a decoration: never a four-screen form whose Save can only fail.
  if (state.data && !state.data.windowOpen) {
    return (
      <FlowOutcomeScreen
        outcome={CHECKIN_CLOSED}
        therapistName={therapistName}
        onBack={() => navigate("/me/home")}
      />
    );
  }
  return <CheckinFlowInner sessionUuid={sessionUuid} />;
}

const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** The session's weekday in the client's STORED tz: read off the offset-bearing
 *  `start_local` date verbatim (never the browser tz), or null before it loads. */
function sessionWeekday(startLocal: string | undefined): string | null {
  const match = startLocal?.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const day = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay();
  return WEEKDAYS[day];
}

function CheckinSaved() {
  const navigate = useNavigate();
  const { sessionUuid = "" } = useParams();
  const session = useSession(sessionUuid);
  return (
    <FlowSavedScreen
      title={CHECKIN_COPY.saved.title(sessionWeekday(session.data?.startLocal))}
      body={CHECKIN_COPY.saved.body}
      homeLabel={CHECKIN_COPY.saved.homeLabel}
      onHome={() => navigate("/me/home")}
    />
  );
}

type Answers = Record<CheckinQuestion["field"], string>;

const EMPTY_ANSWERS: Answers = { onYourMind: "", showedPartnerMatters: "", partnerShowedIMatter: "" };

/** Blank or whitespace-only answers are saved as null (the question was skipped). */
function orNull(text: string): string | null {
  return text.trim() ? text : null;
}

function CheckinFlowInner({ sessionUuid }: { sessionUuid: string }) {
  const navigate = useNavigate();
  const { couple } = useMeContext();
  const therapistName = couple?.therapistName ?? FALLBACK_THERAPIST_NAME;
  const save = useSaveCheckin(sessionUuid);
  const goBack = useFlowBack();
  // WP-2's session read (shared ["me","sessions",uuid] cache entry): only the
  // standing-slot flag for analytics.
  const session = useSession(sessionUuid);

  // 0 = how you're feeling; 1..3 = CHECKIN_COPY.questions[step - 1].
  const [step, setStep] = useState(0);
  const [saved, setSaved] = useState(false);
  const [quadrant, setQuadrant] = useState<MoodQuadrant | null>(null);
  const [word, setWord] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Answers>(EMPTY_ANSWERS);

  const questions = CHECKIN_COPY.questions;

  // Analytics (Family G): counts and flags only — never the mood, word or text.
  // `is_edit` stays on the event for dashboard continuity; answers can no longer be edited.
  const isStanding = session.data?.fromStandingSlot;
  useTrackOnce(
    COUPLE_EVENTS.checkinStarted,
    { session_id: sessionUuid, is_standing: isStanding, is_edit: false },
    !session.isLoading,
  );

  function pickQuadrant(next: MoodQuadrant) {
    if (next === quadrant) return;
    // A new quadrant invalidates the word (its list changes).
    setQuadrant(next);
    setWord(null);
  }

  function submit() {
    if (!quadrant || !word) return;
    const draft: CheckinDraft = {
      moodQuadrant: quadrant,
      feelingWord: word,
      onYourMind: orNull(answers.onYourMind),
      showedPartnerMatters: orNull(answers.showedPartnerMatters),
      partnerShowedIMatter: orNull(answers.partnerShowedIMatter),
    };
    save.mutate(draft, {
      onSuccess: () => {
        track(COUPLE_EVENTS.checkinSubmitted, {
          session_id: sessionUuid,
          is_standing: isStanding,
          is_edit: false,
          questions_answered: 1 + Object.values(answers).filter((text) => text.trim()).length,
        });
        setSaved(true);
      },
    });
  }

  if (saved || isAlreadySubmitted(save.error)) return <CheckinSaved />;

  // Any other 409 is the closed window reached the other way (the session started while the form
  // was open). An answer, not a crash, so it replaces the form; only an unexpected failure reaches
  // `FriendlyError`, carrying the server's reason.
  const failure = classifyCheckinError(save.error);
  if (failure?.kind === "expected") {
    return (
      <FlowOutcomeScreen
        outcome={failure.outcome}
        therapistName={therapistName}
        onBack={() => navigate("/me/home")}
      />
    );
  }
  const saveErrorDetail = unexpectedErrorDetail(failure);

  // One card shell for all four screens (the onboarding `StepShell`): progress line,
  // question, answer, then Back + the primary in the footer. Back on the first
  // screen leaves the flow the way it was entered — back to the hub card that
  // opened it, or Home when a reminder link opened it cold; on the others it
  // pops a step with answers intact.
  const totalSteps = questions.length + 1;
  const onBack = step === 0 ? goBack : () => setStep(step - 1);

  if (step > 0) {
    const question = questions[step - 1];
    const isLast = step === questions.length;
    return (
      <StepShell
        showBack
        onBack={onBack}
        backLabel={CHECKIN_COPY.backLabel}
        currentStep={step}
        totalSteps={totalSteps}
        footer={
          isLast ? (
            <Button disabled={save.isPending} onClick={submit}>
              {CHECKIN_COPY.saveLabel}
            </Button>
          ) : (
            <Button onClick={() => setStep(step + 1)}>{CHECKIN_COPY.continueLabel}</Button>
          )
        }
      >
        <StepHeading title={question.title} />
        <TextField
          multiline
          id={`checkin-${question.field}`}
          label={question.title}
          labelHidden
          hint={CHECKIN_COPY.optionalHint}
          value={answers[question.field]}
          onChange={(value) => setAnswers({ ...answers, [question.field]: value })}
          rows={4}
          placeholder={question.placeholder}
        />
        {isLast ? <FriendlyError detail={saveErrorDetail} /> : null}
      </StepShell>
    );
  }

  return (
    <StepShell
      showBack
      onBack={onBack}
      backLabel={CHECKIN_COPY.backLabel}
      currentStep={0}
      totalSteps={totalSteps}
      footer={
        <Button disabled={!quadrant || !word} onClick={() => setStep(1)}>
          {CHECKIN_COPY.continueLabel}
        </Button>
      }
    >
      <StepHeading title={CHECKIN_COPY.mood.title}>
        <p className="m-0">{CHECKIN_COPY.mood.sub}</p>
      </StepHeading>

      {/* The circles + word choices are ONE group, centered between the heading
          and the privacy line, so the gap above the circles equals the gap below
          the words. 64px (--space-64) between the circles and the words (well over the 20px between
          circles) so the words read as the next group, not a fifth row. The words' space is reserved before a circle is picked (every
          quadrant's chips stacked in one grid cell, only the picked one shown), so
          picking a circle never moves the circles. */}
      <div className="flex flex-1 flex-col justify-center gap-16">
        <MoodQuadrantGrid value={quadrant} onChange={pickQuadrant} />
        <div className="grid">
          {MOOD_QUADRANTS.map((q) => {
            const active = q.key === quadrant;
            return (
              <div
                key={q.key}
                aria-hidden={!active}
                inert={!active}
                className={`col-start-1 row-start-1 flex flex-col gap-3 ${active ? "" : "invisible"}`}
              >
                <span className="text-center text-base font-medium text-[var(--text-secondary)]">
                  {CHECKIN_COPY.mood.wordPrompt}
                </span>
                <WordChips words={q.words} value={active ? word : null} onChange={setWord} allowCustom tone={q.key} align="center" />
              </div>
            );
          })}
        </div>
      </div>

      <p className="m-0 text-center text-xs leading-[1.5] text-[var(--text-tertiary)]">
        {privacyLine(therapistName)}
      </p>
    </StepShell>
  );
}
